import BulbOutlined from '@ant-design/icons/BulbOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import CopyOutlined from '@ant-design/icons/CopyOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import DislikeFilled from '@ant-design/icons/DislikeFilled';
import DislikeOutlined from '@ant-design/icons/DislikeOutlined';
import LikeFilled from '@ant-design/icons/LikeFilled';
import LikeOutlined from '@ant-design/icons/LikeOutlined';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import ReloadOutlined from '@ant-design/icons/ReloadOutlined';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Card from 'antd/es/card';
import Popconfirm from 'antd/es/popconfirm';
import theme from 'antd/es/theme';
import Timeline from 'antd/es/timeline';
import Tooltip from 'antd/es/tooltip';
import Typography from 'antd/es/typography';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import ThinkingProgress, { type Thought } from './ThinkingProgress';

import MarkdownContent from '@components/MarkdownContent';
import { useAgentRunsQuery } from '@queries/agent-runs';
import { track } from '@utils/mixpanel';

const StyledCard = styled(Card)<{ $inverted?: boolean }>`
  .ant-card-body {
    padding: 0.5rem;
    margin-bottom: 0;
  }

  ${({ $inverted }) =>
    $inverted &&
    `
    .ant-typography,
    .markdown-body {
      color: white !important;
    }
    .ant-btn {
      color: rgba(255, 255, 255, 0.85);
      &:hover {
        color: white;
      }
    }
  `}
`;

const ThoughtsContainer = styled.div`
  margin-top: 12px;
  margin-bottom: 12px;
  padding: 12px;
  background-color: ${(props) => props.theme.token.colorFillSecondary};
  border-radius: ${(props) => props.theme.token.borderRadiusLG}px;
  border: 1px solid ${(props) => props.theme.token.colorBorderSecondary};
  animation: fadeIn 0.3s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ThoughtsButton = styled(Button)<{
  $active?: boolean;
  $inverted?: boolean;
}>`
  display: flex;
  align-items: center;

  .anticon {
    margin-right: 4px;
  }
`;

const StyledTimeline = styled(Timeline)`
  margin: 0;
  padding: 0;

  .ant-timeline-item:last-child {
    padding-bottom: 0;
  }

  .ant-timeline-item:last-child .ant-timeline-item-content {
    min-height: auto;
  }
`;

const TasksContainer = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${(props) => props.theme.token.colorBorderSecondary};
`;

const TasksStatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 16px;
  background-color: ${(props) => props.theme.token.colorBgContainer};
  border: 1px solid ${(props) => props.theme.token.colorBorder};
  font-size: 12px;
`;

// Utility function to strip markdown links and text in square brackets from content
const stripMarkdownLinks = (content: string): string => {
  // First replace markdown links [text](url) with just the text
  let result = content.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  // Then remove any remaining text in square brackets
  result = result.replace(/\[[^\]]+\]/g, '');
  return result;
};

const Message = ({
  messageId,
  role,
  content,
  requestedModel,
  thoughts,
  createdAt,
  onDeleteMessage,
  isRegenerating,
  onRegenerate,
  isStreaming,
  inverted,
  tasks,
}: {
  messageId: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  requestedModel?: 'basic' | 'standard' | 'thorough' | null;
  thoughts?: Array<Thought>;
  createdAt?: string;
  onDeleteMessage: () => Promise<void>;
  isRegenerating?: boolean;
  onRegenerate?: () => Promise<void>;
  isStreaming?: boolean;
  inverted?: boolean;
  tasks: Array<{
    taskType: 'agent_run';
    agentRunId: string;
  }>;
}) => {
  const {
    token: { colorPrimary },
  } = theme.useToken();
  const { message } = App.useApp();
  const [isDeleting, setIsDeleting] = useState(false);
  const [feedback, setFeedback] = useState<'like' | 'dislike' | null>(null);
  const [isThoughtsExpanded, setIsThoughtsExpanded] = useState(false);

  const hasThoughts = thoughts && thoughts.length > 0;

  // Get agent run IDs from tasks
  const taskAgentRunIds =
    tasks
      ?.filter((task) => task.taskType === 'agent_run')
      .map((task) => task.agentRunId) || [];

  // Fetch agent runs
  const agentRunsQuery = useAgentRunsQuery(
    {
      agentRunIds: taskAgentRunIds,
    },
    taskAgentRunIds.length > 0,
  );

  const isLoadingAgentRuns = agentRunsQuery.isLoading;
  const agentRuns = agentRunsQuery.data ?? [];

  // Calculate agent run status
  const getAgentRunStatus = (run: {
    startedAt: Date | null;
    cancelledAt: Date | null;
    succeededAt: Date | null;
    failedAt: Date | null;
  }) => {
    if (run.succeededAt) return 'succeeded';
    if (run.failedAt) return 'failed';
    if (run.cancelledAt) return 'cancelled';
    if (run.startedAt) return 'running';
    return 'pending';
  };

  // Calculate task counts
  const runningTasksCount = agentRuns.filter(
    (run) => getAgentRunStatus(run) === 'running',
  ).length;
  const completedTasksCount = agentRuns.filter(
    (run) => getAgentRunStatus(run) === 'succeeded',
  ).length;
  const hasRunningTasks = runningTasksCount > 0;
  const hasTasks =
    agentRuns.length > 0 || (taskAgentRunIds.length > 0 && isLoadingAgentRuns);

  // Calculate progress
  const progress = useMemo(() => {
    const expectedMaxThoughts =
      (requestedModel === 'thorough' && 12) ||
      (requestedModel === 'standard' && 6) ||
      2;
    const currentThoughts = Math.max((thoughts?.length ?? 0) - 1, 0);

    // current thoughts must always be less than max thoughts, so in the case that we exceed max thoughts, we just set max thoughts equal to currentThoughts + 1
    if (currentThoughts > expectedMaxThoughts) {
      return Math.round((currentThoughts / (currentThoughts + 1)) * 100);
    }

    return Math.round((currentThoughts / expectedMaxThoughts) * 100);
  }, [requestedModel, thoughts]);

  return (
    <StyledCard $inverted={inverted}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <Typography.Text type="secondary" strong>
            {role === 'assistant' ? 'Assistant' : 'You'}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ marginLeft: '0.5rem' }}>
            {createdAt ? new Date(createdAt).toLocaleString() : ''}
          </Typography.Text>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {role === 'assistant' && (
              <>
                {hasThoughts && !isStreaming && (
                  <ThoughtsButton
                    type={isThoughtsExpanded ? 'primary' : 'text'}
                    size="small"
                    icon={<BulbOutlined />}
                    onClick={() => setIsThoughtsExpanded(!isThoughtsExpanded)}
                    $inverted={inverted}
                  >
                    Thoughts
                  </ThoughtsButton>
                )}
                <Tooltip title="I liked this answer">
                  <Button
                    size="small"
                    shape="circle"
                    type="text"
                    icon={
                      feedback === 'like' ? <LikeFilled /> : <LikeOutlined />
                    }
                    style={{
                      color: feedback === 'like' ? colorPrimary : undefined,
                    }}
                    onClick={() => {
                      setFeedback('like');
                      track('LIKED_ASSISTANT_MESSAGE', {
                        conversationMessageId: messageId,
                      });
                    }}
                  />
                </Tooltip>
                <Tooltip title="I disliked this answer">
                  <Button
                    size="small"
                    shape="circle"
                    type="text"
                    icon={
                      feedback === 'dislike' ? (
                        <DislikeFilled />
                      ) : (
                        <DislikeOutlined />
                      )
                    }
                    style={{
                      color: feedback === 'dislike' ? colorPrimary : undefined,
                    }}
                    onClick={() => {
                      setFeedback('dislike');
                      track('DISLIKED_ASSISTANT_MESSAGE', {
                        conversationMessageId: messageId,
                      });
                    }}
                  />
                </Tooltip>
              </>
            )}
          </div>
          {onRegenerate && (
            <Button
              size="small"
              shape="circle"
              type="text"
              loading={isRegenerating}
              icon={<ReloadOutlined />}
              onClick={onRegenerate}
            />
          )}
          <Tooltip title="Copy message">
            <Button
              size="small"
              shape="circle"
              type="text"
              icon={<CopyOutlined />}
              onClick={() => {
                const plainText = stripMarkdownLinks(content);
                navigator.clipboard
                  .writeText(plainText)
                  .then(() => {
                    void message.success('Text copied to clipboard');
                    void track('COPIED_MESSAGE_TEXT', {
                      conversationMessageId: messageId,
                    });
                  })
                  .catch(() => {
                    void message.error('Failed to copy text to clipboard');
                  });
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this message?"
            onConfirm={async () => {
              setIsDeleting(true);
              try {
                await onDeleteMessage();
              } catch (e) {
                await message.error(
                  'Failed to delete message. Please try again.',
                );
              } finally {
                setIsDeleting(false);
              }
            }}
            icon={<DeleteOutlined />}
            okText="Yes"
            cancelText="No"
            placement={role === 'user' ? 'topLeft' : 'topRight'}
          >
            <Button
              size="small"
              shape="circle"
              type="text"
              loading={isDeleting}
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </div>
      </div>

      {/* Thoughts section - now above the message content */}
      {role === 'assistant' && hasThoughts && isThoughtsExpanded && (
        <ThoughtsContainer>
          <Typography.Text
            type="secondary"
            strong
            style={{
              display: 'block',
              marginBottom: '8px',
              color: inverted ? 'rgba(255, 255, 255, 0.65)' : undefined,
            }}
          >
            Thoughts
          </Typography.Text>
          <StyledTimeline
            items={thoughts.map((thought) => ({
              color: colorPrimary,
              children: (
                <Typography.Text
                  style={{
                    color: inverted ? 'rgba(255, 255, 255, 0.85)' : undefined,
                  }}
                >
                  {thought.text}
                </Typography.Text>
              ),
            }))}
          />
        </ThoughtsContainer>
      )}

      {content.length > 0 ? (
        <MarkdownContent
          content={content}
          isStreaming={isStreaming}
          showReferences={role === 'assistant'}
        />
      ) : (
        <ThinkingProgress
          thoughts={thoughts}
          isStreaming={isStreaming}
          inverted={inverted}
          progress={progress}
        />
      )}

      {hasTasks && (
        <TasksContainer>
          <TasksStatusBadge>
            {isLoadingAgentRuns && (
              <LoadingOutlined style={{ color: colorPrimary }} />
            )}
            {!isLoadingAgentRuns && hasRunningTasks && (
              <LoadingOutlined style={{ color: colorPrimary }} />
            )}
            {!isLoadingAgentRuns && !hasRunningTasks && (
              <CheckCircleOutlined style={{ color: colorPrimary }} />
            )}
            <Typography.Text type="secondary">
              {isLoadingAgentRuns
                ? 'Loading tasks...'
                : `${completedTasksCount} completed / ${runningTasksCount} running tasks`}
            </Typography.Text>
          </TasksStatusBadge>
        </TasksContainer>
      )}
    </StyledCard>
  );
};

export default Message;
